import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Zap, Star, Crown, Rocket, History, ArrowRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PACKAGE_ICONS = {
  starter: Zap,
  basic: Star,
  professional: Crown,
  enterprise: Rocket,
};

const PACKAGE_COLORS = {
  starter: { bg: 'bg-sky-50', border: 'border-sky-200', accent: 'text-sky-700', btnBg: 'bg-sky-600 hover:bg-sky-700' },
  basic: { bg: 'bg-indigo-50', border: 'border-indigo-200', accent: 'text-indigo-700', btnBg: 'bg-indigo-600 hover:bg-indigo-700' },
  professional: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-700', btnBg: 'bg-amber-600 hover:bg-amber-700', popular: true },
  enterprise: { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-700', btnBg: 'bg-emerald-600 hover:bg-emerald-700' },
};

const CreditPurchase = () => {
  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pkgRes, balRes] = await Promise.all([
        axios.get(`${API}/payments/packages`),
        axios.get(`${API}/subscription/balance`).catch(() => ({ data: null })),
      ]);
      setPackages(pkgRes.data.packages || []);
      setBalance(balRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const res = await axios.get(`${API}/payments/history`);
      setPayments(res.data || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
    setShowHistory(true);
  };

  const handleCheckout = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const formData = new FormData();
      formData.append('package_id', packageId);
      formData.append('origin_url', window.location.origin);

      const res = await axios.post(`${API}/payments/checkout`, formData);

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Checkout failed';
      toast.error(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="credit-purchase-loading">
          <Loader2 className="animate-spin text-indigo-600" size={32} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="credit-purchase-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Purchase Credits
            </h1>
            <p className="text-slate-600">Choose a package to power your assessments</p>
          </div>
          <Button variant="outline" onClick={fetchPaymentHistory} data-testid="view-payment-history-btn">
            <History size={16} className="mr-2" />
            Payment History
          </Button>
        </div>

        {/* Current Balance */}
        {balance && (
          <Card className="p-5 border border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-indigo-50" data-testid="current-balance-display">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <CreditCard size={28} className="text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-indigo-600 font-medium">Current Balance</p>
                  <p className="text-3xl font-bold text-indigo-900">{balance.credits} <span className="text-base font-normal text-indigo-500">credits</span></p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">{balance.subscription_name}</p>
                <Badge variant={balance.status === 'active' ? 'default' : 'secondary'} className="mt-1">
                  {balance.status}
                </Badge>
              </div>
            </div>
          </Card>
        )}

        {/* Pricing info */}
        <div className="flex items-center gap-6 px-4 py-3 bg-slate-50 rounded-lg border border-slate-200">
          <span className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">2 credits</span> per AI assessment
          </span>
          <span className="w-px h-4 bg-slate-300" />
          <span className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">1 credit</span> per manual audit
          </span>
        </div>

        {/* Package Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {packages.map((pkg) => {
            const colors = PACKAGE_COLORS[pkg.id] || PACKAGE_COLORS.starter;
            const Icon = PACKAGE_ICONS[pkg.id] || Zap;
            const perCredit = (pkg.price / pkg.credits).toFixed(2);
            const isLoading = checkoutLoading === pkg.id;

            return (
              <Card
                key={pkg.id}
                className={`relative p-6 border-2 ${colors.border} ${colors.bg} transition-all hover:shadow-lg hover:-translate-y-1`}
                data-testid={`package-card-${pkg.id}`}
              >
                {colors.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-600 text-white text-xs px-3 py-0.5">Most Popular</Badge>
                  </div>
                )}
                <div className="text-center space-y-4">
                  <div className={`mx-auto w-14 h-14 rounded-xl flex items-center justify-center ${colors.bg} border ${colors.border}`}>
                    <Icon size={28} className={colors.accent} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{pkg.name}</h3>
                    <p className={`text-3xl font-bold mt-1 ${colors.accent}`}>
                      ${pkg.price.toFixed(2)}
                    </p>
                  </div>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center justify-center gap-2">
                      <Check size={14} className="text-emerald-500" />
                      <span><span className="font-semibold text-slate-800">{pkg.credits}</span> credits</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Check size={14} className="text-emerald-500" />
                      <span>${perCredit} per credit</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Check size={14} className="text-emerald-500" />
                      <span>{Math.floor(pkg.credits / 2)} AI assessments</span>
                    </div>
                  </div>
                  <Button
                    className={`w-full ${colors.btnBg} text-white`}
                    onClick={() => handleCheckout(pkg.id)}
                    disabled={isLoading || !balance}
                    data-testid={`checkout-btn-${pkg.id}`}
                  >
                    {isLoading ? (
                      <Loader2 size={16} className="animate-spin mr-2" />
                    ) : (
                      <ArrowRight size={16} className="mr-2" />
                    )}
                    {isLoading ? 'Redirecting...' : 'Buy Now'}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {!balance && (
          <Card className="p-6 border border-rose-200 bg-rose-50 text-center" data-testid="no-subscription-warning">
            <p className="text-rose-700 font-medium">No active subscription found</p>
            <p className="text-sm text-rose-600 mt-1">Contact your admin to get a subscription assigned before purchasing credits.</p>
          </Card>
        )}

        {/* Payment History */}
        {showHistory && (
          <Card className="p-6 border border-slate-200" data-testid="payment-history-section">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Payment History</h3>
            {payments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payment-history-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-left">
                      <th className="py-3 px-4 font-semibold text-slate-700">Package</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Credits</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Amount</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Status</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={p.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-slate-800 font-medium">{p.package?.name || p.package_id}</td>
                        <td className="py-3 px-4 text-slate-600">{p.credits}</td>
                        <td className="py-3 px-4 text-slate-600">${p.amount?.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <Badge
                            variant={p.payment_status === 'paid' ? 'default' : p.payment_status === 'pending' ? 'secondary' : 'destructive'}
                          >
                            {p.payment_status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-slate-500">{new Date(p.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-slate-500 py-6">No payment history yet</p>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CreditPurchase;
